import {Euler, PerspectiveCamera, Vector3} from "three";
import {EaseHelper} from "features/apartmentViewer/ApartmentViewer/helpers/EaseHelper";
import {CameraModel} from "features/apartmentViewer/ApartmentViewer/models/CameraModel";
import {TimeService} from "features/apartmentViewer/ApartmentViewer/services/TimeService";
import {CanvasService} from "features/apartmentViewer/ApartmentViewer/services/CanvasService";
import {VectorHelper} from "features/apartmentViewer/ApartmentViewer/helpers/VectorHelper";

export class CameraHandler {

    private _camera: PerspectiveCamera;
    private _siteCamera: CameraModel | undefined;
    private _targetPosition: Vector3;  // Target position for smooth interpolation
    private _targetRotation: Euler;    // Target rotation for smooth interpolation
    private _targetFov: number;        // Target FOV for smooth interpolation
    private _onCameraRotationChange: (yAxis: number) => void = () => {};
    private _lastFrameRotation: number;


    constructor(
        private timeService: TimeService,
        private canvasService: CanvasService,
    ) {

        this._camera = new PerspectiveCamera();
        this._camera.rotation.order = "YXZ";
        this._camera.fov = 90; // Default FOV to start with (ignoring any external config)
        this._camera.updateProjectionMatrix();

        // Initialize target position, rotation, and FOV to match the initial camera state
        this._targetPosition = new Vector3().copy(this._camera.position);
        this._targetRotation = new Euler().copy(this._camera.rotation);
        this._lastFrameRotation = this._camera.rotation.y;
        this._targetFov = this._camera.fov;
    }

    setCamera(cameraConfig: CameraModel, skipRotation = false) {
        this._siteCamera = cameraConfig;

        // Set the target position, rotation, and FOV from the siteCamera data
        this._targetPosition.copy(cameraConfig.position);

        if (!skipRotation) {
            this._targetRotation.copy(cameraConfig.rotation);
        }

        // Maintain the current FOV unless explicitly changed
        // Since the initial load shouldn't cause an undesired rotation, we will also directly set camera values
        this._camera.position.copy(this._targetPosition);
        if (!skipRotation) {
            this._camera.rotation.copy(this._targetRotation);
        }
        this._camera.fov = this._targetFov;

        // Immediately update the projection matrix to reflect the initial setup
        this._camera.updateProjectionMatrix();
    }

    getCameraRotation() {
        return this._camera.rotation.y * VectorHelper.RadToDeg;
    }

    update() {
        const t = 1 - Math.pow(1 - 0.9999, this.timeService.deltaTime);

        // Smoothly interpolate the camera position and rotation towards the target
        this._camera.position.lerp(this._targetPosition, t);

        this._camera.rotation.x = EaseHelper.lerpAngleRadians(this._camera.rotation.x, this._targetRotation.x, t);
        this._camera.rotation.y = EaseHelper.lerpAngleRadians(this._camera.rotation.y, this._targetRotation.y, t);
        this._camera.rotation.z = EaseHelper.lerpAngleRadians(this._camera.rotation.z, this._targetRotation.z, t);

        // Smoothly interpolate the field of view (if applicable)
        this._camera.fov += (this._targetFov - this._camera.fov) * t;

        // Update the aspect ratio and projection matrix
        this._camera.aspect = this.canvasService.width / this.canvasService.height;
        this._camera.updateProjectionMatrix();

        if (this.shouldUpdateRotation()) {
            this._lastFrameRotation = this._camera.rotation.y;
            if (this._onCameraRotationChange) this._onCameraRotationChange(this.getCameraRotation());
        }
    }

    shouldUpdateRotation() {
        const rotationDiff = Math.abs(this._camera.rotation.y - this._lastFrameRotation) * VectorHelper.RadToDeg;


        return rotationDiff > 1;
    }

    get camera(): PerspectiveCamera {
        return this._camera;
    }

    set camera(value: PerspectiveCamera) {
        this._camera = value;
    }

    get siteCamera(): CameraModel | undefined {
        if(!this._siteCamera) return undefined;

        return this._siteCamera;
    }

    set siteCamera(value: CameraModel) {
        this._siteCamera = value;
    }

    get targetFov(): number {
        return this._targetFov;
    }

    set targetFov(value: number) {
        this._targetFov = value;
    }

    get targetRotation(): Euler {
        return this._targetRotation;
    }

    set targetRotation(value: Euler) {
        this._targetRotation = value;
    }

    get targetPosition(): Vector3 {
        return this._targetPosition;
    }

    set targetPosition(value: Vector3) {
        this._targetPosition = value;
    }

    set onCameraRotationChange(value: (yAxis: number) => void) {
        this._onCameraRotationChange = value;
    }
}
